import { classnames } from 'Utils/classnames/classnames.utils';
import React, { Fragment, useContext } from 'react';
import { useFormikContext } from 'formik';
import type { ReservationBlockFormValues } from 'Src/types/reservation';
import { windowWrapper } from '@clubmed/utils';
import { getAttendeeLabel } from 'Components/widgets/reservation-block/reservationBlock.utils';
import { ServicesContext } from 'Context/ServicesContext';
import { useAppContext } from 'Context/ReactAppContext';
import { t } from 'Src/i18n';
import { FormikInput } from 'Components/frameworks/formik/FormikInput';
const TIMESLOT_REGEX = /([a-z0-9_]+)-([0-9]{8})-([0-9]{8})-([a-z])/;
type Props = {
  attendees: ReadonlyArray<AttendeeWithSchedulesType>;
  hasReservedSchedule: boolean;
  cancellingActivity: boolean;
  setCancellingActivity: (arg0: boolean) => void;
  getLowerPrice: (arg0: AttendeeWithSchedulesType) => PriceType;
};
export const ReservationAttendeesStep = ({
  attendees,
  hasReservedSchedule,
  cancellingActivity,
  setCancellingActivity,
  getLowerPrice,
}: Props) => {
  const appContext = useAppContext();
  const { attendeesList } = useContext(ServicesContext);
  const { values, setFieldValue } = useFormikContext<ReservationBlockFormValues>();

  const checkAllAttendees = ({ target }) => {
    Object.keys(values.attendees).forEach((attendeeId) => {
      windowWrapper.requestAnimationFrame(() => {
        setFieldValue(`attendees.${attendeeId}.selected`, target.checked);
      });
    });

    if (hasReservedSchedule && !target.checked) {
      setCancellingActivity(true);
    } else if (cancellingActivity && target.checked) {
      setCancellingActivity(false);
    }
  };

  const uncheckOneAttendee = (attendee: AttendeeWithSchedulesType, target) => {
    if (cancellingActivity && target.checked) {
      setCancellingActivity(false);
    } else if (!target.checked) {
      // make sure alls slots are unchecked on attendee unselection
      Object.keys(values.slots).forEach((slotKey) => {
        const timeSlotRegex = new RegExp(TIMESLOT_REGEX, 'gi');
        const [, , , , timeSlotCustomerId] = timeSlotRegex.exec(slotKey);

        if (timeSlotCustomerId && timeSlotCustomerId === attendee.id) {
          setFieldValue(`slots.${slotKey}.isSelected`, false);
        }
      });

      if (
        hasReservedSchedule &&
        !Object.entries(values.attendees).some(
          ([attendeeID, { selected }]) => attendeeID !== attendee.id && selected,
        )
      ) {
        setCancellingActivity(true);
      }
    }
  };

  return (
    <Fragment>
      {Object.values(values.attendees).length > 1 && (
        <div className="border-b-1 border-lightGrey py-15">
          <FormikInput
            label={t('booking.activities.skiEquipment.sameScheduleAllParticipants')}
            name="sameScheduleForAll"
            onChange={checkAllAttendees}
            type="checkbox"
          />
        </div>
      )}
      {attendees.map((attendee) => {
        const lowerPrice = getLowerPrice(attendee);
        return (
          <div className="py-10" key={attendee.id}>
            <FormikInput
              isDisabled={attendee.schedules.every(({ isFullyPaid }) => isFullyPaid)}
              label={
                <Fragment>
                  <span className="air360-replay-ignore" data-cs-mask>
                    {getAttendeeLabel(attendeesList, attendee.id, appContext)}
                  </span>
                  {lowerPrice.formattedInitialValue && (
                    <Fragment>
                      <span className="ms-20 font-sans text-12 font-normal text-black">
                        {t('price.format.from')}
                      </span>{' '}
                      <s className="ms-10 text-sienna line-through">
                        {lowerPrice.formattedInitialValue}
                      </s>
                    </Fragment>
                  )}
                  <span className={classnames('px-10 text-black')}>
                    {lowerPrice.formattedValue}
                  </span>
                </Fragment>
              }
              name={`attendees.${attendee.id}.selected`}
              onChange={(e) => uncheckOneAttendee(attendee, e.target)}
              type="checkbox"
            />
          </div>
        );
      })}
    </Fragment>
  );
};
